import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formsApi } from '../api/api';
import { Form } from '../types/form';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useFormFiltering } from '../hooks/useFormFiltering';

import { toast } from 'sonner';
import { useNotification } from '../contexts/NotificationContext';
import { Plus, Edit, BarChart3, ExternalLink, Trash2, Copy, CopyPlus, UserCheck, Search, Clock, Eye, Calendar, Users, BookOpen } from 'lucide-react';
import { FormCardSkeleton } from '../components/ui/skeleton';
import { useTranslation } from 'react-i18next';

const Dashboard: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [forms, setForms] = useState<Form[]>([]);
  const [sharedForms, setSharedForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'name'>('updated');
  const [activeTab, setActiveTab] = useState<'your' | 'shared'>('your');
  const { showConfirm } = useNotification();
  const { handleApiError } = useErrorHandler();

  const fetchForms = useCallback(async () => {
    try {
      const response = await formsApi.getForms();
      
      // Ensure we have an array
      if (Array.isArray(response.data)) {
        setForms(response.data);
      } else if (response.data && typeof response.data === 'object' && 'results' in response.data && Array.isArray((response.data as any).results)) {
        // Handle paginated response
        setForms((response.data as any).results);
      } else {
          setForms([]);
      }
    } catch (error) {
      handleApiError(error as any, 'fetch forms');
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const fetchSharedForms = useCallback(async () => {
    try {
      const response = await formsApi.getSharedForms();
      
      // Ensure we have an array
      if (Array.isArray(response.data)) {
        setSharedForms(response.data);
      } else if (response.data && typeof response.data === 'object' && 'results' in response.data && Array.isArray((response.data as any).results)) {
        // Handle paginated response
        setSharedForms((response.data as any).results);
      } else {
        setSharedForms([]);
      }
    } catch (error) {
      handleApiError(error as any, 'fetch shared forms');
      setSharedForms([]);
    } finally {
      setSharedLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    fetchForms();
    fetchSharedForms();
  }, [location.pathname, fetchForms, fetchSharedForms]);

  const currentFormsList = useMemo(() => 
    activeTab === 'your' ? forms : sharedForms, 
    [activeTab, forms, sharedForms]
  );

  const filteredAndSortedForms = useFormFiltering(currentFormsList, {
    searchQuery,
    statusFilter,
    sortBy
  });

  const handleDeleteForm = async (id: string) => {
    const confirmed = await showConfirm({
      title: t('dashboard.deleteFormTitle'),
      message: t('dashboard.deleteFormMessage'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      onConfirm: () => {},
      onCancel: () => {}
    });

    if (confirmed) {
      try {
        await formsApi.deleteForm(id);
        setForms(forms.filter(form => form.id !== id));
        toast.success(t('dashboard.toastDeletedTitle'), {
          description: t('dashboard.toastDeletedDesc')
        });
      } catch (error) {
        toast.error(t('dashboard.toastDeleteErrorTitle'), {
          description: t('dashboard.toastDeleteErrorDesc')
        });
      }
    }
  };

  const handleDuplicateForm = async (id: string) => {
    try {
      await formsApi.duplicateForm(id);
      toast.success(t('dashboard.toastDuplicatedTitle', 'Form duplicated'), {
        description: t('dashboard.toastDuplicatedDesc', 'A copy was created as a draft.'),
      });
      fetchForms();
    } catch (error) {
      toast.error(t('dashboard.toastDuplicateErrorTitle', 'Could not duplicate form'), {
        description: t('dashboard.toastDuplicateErrorDesc', 'Please try again.'),
      });
    }
  };

  const copyFormLink = (form: Form) => {
    const identifier = form.public_identifier || form.slug || form.id;
    const link = `${window.location.origin}/public/${identifier}`;

    const fallbackCopy = () => {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success(t('dashboard.toastLinkCopiedTitle'), {
          description: t('dashboard.toastLinkCopiedDesc')
        });
      } catch (err) {
        toast.error(t('dashboard.toastCopyFailedTitle'), {
          description: t('dashboard.toastCopyFailedDesc')
        });
      } finally {
        document.body.removeChild(textarea);
      }
    };

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(link)
        .then(() => {
          toast.success(t('dashboard.toastLinkCopiedTitle'), {
            description: t('dashboard.toastLinkCopiedDesc')
          });
        })
        .catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return t('dashboard.today');
    if (diffDays === 1) return t('dashboard.yesterday');
    if (diffDays < 7) return t('dashboard.daysAgo', { count: diffDays });
    if (diffDays < 30) return t('dashboard.weeksAgo', { count: Math.floor(diffDays / 7) });
    return date.toLocaleDateString();
  };

  const renderFormCard = (form: Form, isSharedForm = false) => (
    <Card 
      key={form.id} 
      className="group notion-transition notion-hover-lift hover:shadow-lg hover:border-border/60 bg-card border-border/60 notion-fade-in"
    >
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="heading-3 truncate">{form.title}</CardTitle>
                <div className="flex items-center gap-1">
                  {form.is_published ? (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        {t('dashboard.badgePublished')}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        {t('dashboard.badgeDraft')}
                      </span>
                    </div>
                  )}
                  {form.shared && (
                    <span className="text-xs bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
                      {t('dashboard.badgeShared')}
                    </span>
                  )}
                </div>
              </div>
              <CardDescription className="body-medium line-clamp-2 mb-3">
                {form.description || t('dashboard.noDescription')}
              </CardDescription>
              
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  <span>{t('dashboard.fieldsCount', { count: form.fields.length })}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{t('dashboard.updated', { time: formatRelativeTime(form.updated_at) })}</span>
                </div>
                {isSharedForm && (
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span>{t('dashboard.byUser', { user: form.created_by })}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          <div className="flex items-center justify-between notion-transition opacity-0 group-hover:opacity-100">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{t('dashboard.created', { time: formatRelativeTime(form.created_at) })}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Link to={`/forms/${form.id}/edit`}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10">
                  <Edit className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={`/forms/${form.id}/submissions`}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10">
                  <BarChart3 className="w-4 h-4" />
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-primary/10"
                onClick={() => {
                  const identifier = form.public_identifier || form.slug || form.id;
                  form.has_public_link && window.open(`/public/${identifier}`, '_blank');
                }}
                disabled={!form.has_public_link}
                title={form.has_public_link ? t('dashboard.openPublic') : t('dashboard.needsPublishView')}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-primary/10"
                onClick={() => form.has_public_link && copyFormLink(form)}
                disabled={!form.has_public_link}
                title={form.has_public_link ? t('dashboard.copyLink') : t('dashboard.needsPublishCopy')}
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Link to={`/forms/${form.id}/edit?tab=links`}>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-primary/10"
                  title={t('dashboard.manageCustomLinks')}
                >
                  <UserCheck className="w-4 h-4" />
                </Button>
              </Link>
              {!isSharedForm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-primary/10"
                  onClick={() => handleDuplicateForm(form.id)}
                  title={t('dashboard.duplicate', 'Duplicate form')}
                >
                  <CopyPlus className="w-4 h-4" />
                </Button>
              )}
              {!isSharedForm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-destructive/10 text-destructive"
                  onClick={() => handleDeleteForm(form.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
  );

  const currentLoading = activeTab === 'your' ? loading : sharedLoading;
  const currentForms = filteredAndSortedForms;

  return (
    <div className="container mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="heading-1">{t('dashboard.title')}</h1>
          <p className="body-medium text-muted-foreground mt-2">{t('dashboard.subtitle')}</p>
        </div>
        <Link to="/forms/new">
          <Button className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.createForm')}
          </Button>
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">
        <div className="flex items-center gap-6">
          <button
            onClick={() => setActiveTab('your')}
            className={`pb-2 border-b-2 transition-colors ${
              activeTab === 'your'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('dashboard.yourFormsTab')}
            {forms.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-muted rounded-full">
                {forms.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('shared')}
            className={`pb-2 border-b-2 transition-colors ${
              activeTab === 'shared'
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('dashboard.sharedFormsTab')}
            {sharedForms.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-muted rounded-full">
                {sharedForms.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('dashboard.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full md:w-64"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dashboard.statusAll')}</SelectItem>
                <SelectItem value="published">{t('dashboard.statusPublished')}</SelectItem>
                <SelectItem value="draft">{t('dashboard.statusDraft')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="updated">{t('dashboard.sortUpdated')}</SelectItem>
                <SelectItem value="created">{t('dashboard.sortCreated')}</SelectItem>
                <SelectItem value="name">{t('dashboard.sortName')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {currentLoading ? (
        <div>
          <div className="sr-only" role="status" aria-live="polite">
            Loading {activeTab === 'your' ? 'your forms' : 'shared forms'}...
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" aria-hidden="true">
            {[...Array(6)].map((_, i) => (
              <FormCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : currentForms.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto w-20 h-20 bg-muted/50 rounded-full flex items-center justify-center mb-4">
            {activeTab === 'your' ? (
              <Plus className="w-10 h-10 text-muted-foreground" />
            ) : (
              <UserCheck className="w-10 h-10 text-muted-foreground" />
            )}
          </div>
          <h3 className="heading-2 mb-3">
            {activeTab === 'your' 
              ? (searchQuery ? t('dashboard.emptyNoFormsFound') : t('dashboard.emptyNoFormsYet'))
              : (searchQuery ? t('dashboard.emptyNoSharedFormsFound') : t('dashboard.emptyNoSharedForms'))
            }
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {activeTab === 'your' 
              ? (searchQuery 
                  ? t('dashboard.emptySearchAdjust')
                  : t('dashboard.emptyCreateFirst')
                )
              : (searchQuery
                  ? t('dashboard.emptySharedSearchAdjust')
                  : t('dashboard.emptySharedInfo')
                )
            }
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {activeTab === 'your' && !searchQuery && (
              <Link to="/forms/new">
                <Button size="lg">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.btnCreateFirst')}
                </Button>
              </Link>
            )}
            
            {searchQuery && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setSortBy('updated');
                }}
                size="lg"
              >
                {t('dashboard.btnClearSearch')}
              </Button>
            )}
            
            {activeTab === 'your' && !searchQuery && (
              <Link to="/knowledge">
                <Button variant="outline" size="lg">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {t('dashboard.btnExploreKnowledge')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentForms.map((form) => renderFormCard(form, activeTab === 'shared'))}
        </div>
      )}

      {!currentLoading && currentForms.length > 0 && (
        <div className="mt-8 text-center text-sm text-muted-foreground">
          {t('dashboard.showingCount', { shown: currentForms.length, total: activeTab === 'your' ? forms.length : sharedForms.length })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;